import { useAddAlert } from '@/hooks/useAddAlert'
import Button from '@UI/New/Button'
import { FC, useEffect, useMemo, useState } from 'react'
import mclasses from '../../styles/modal.module.css'
import classes from './index.module.css'
import { IcoRemove } from '@/utils/icons'
import cn from 'classnames'
import { crmApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { Field, RequestCreateLead, InsertLeadApiLead } from '@/api/types/crm/crm.types'
import { CheckBoxLabel } from '@UI/CheckBox'
import VariableLeadField from '../VariableLeadField/VariableLeadField'
import Select from '../base/Select/Select'
import { ignoreLeadFields, validateForm } from './validateLeadData'
import { useCRMContext } from '../../CRMContext'
import useRoles from '@/hooks/useRoles'
import useFranchises from '@/hooks/useFranchises'

interface CreateOneModalContentProps {
  defData?: {
    NAME?: string | null
    PHONE?: string | null
    EMAIL?: string | null
    PARENTNAME?: string | null
    PARENTEMAIL?: string | null
    PARENTPHONE?: string | null
  }
  onClose: () => void
  onSuccess: (lead?: InsertLeadApiLead | null) => void
}

const CreateOneModalContent: FC<CreateOneModalContentProps> = ({
  defData,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()
  const { isFranchiseManager } = useRoles()
  const {
    enabled: franchisesEnabled,
    options: franchiseOptions,
    autoSelect: autoSelectFranchise,
    loading: franchisesLoading,
  } = useFranchises()
  const {
    preloadingData: { fields, managers, funnels },
  } = useCRMContext()

  const [formData, setFormData] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!defData) return
    const filteredDefData = Object.entries(defData).reduce(
      (acc, [key, value]) => {
        if (value !== null && value !== undefined) {
          acc[key] = value
        }
        return acc
      },
      {} as Record<string, string>,
    )
    setFormData((prev) => ({ ...prev, ...filteredDefData }))
  }, [defData])

  const [addManagerId, setAddManagerId] = useState<false | null | number>(false)
  const [selectedFunnelId, setSelectedFunnelId] = useState<number | null>(null)
  const [selectedFranchiseId, setSelectedFranchiseId] = useState<number | null>(null)

  useEffect(() => {
    if (autoSelectFranchise) setSelectedFranchiseId(Number(autoSelectFranchise))
  }, [autoSelectFranchise])

  const visibleFields = useMemo(
    () =>
      fields.filter(
        (field) =>
          field.is_active && !ignoreLeadFields.includes(field.unique_name),
      ),
    [fields],
  )

  const funnelOptions = useMemo(
    () =>
      [...funnels]
        .sort((a, b) => (a.sorting ?? Number.MAX_SAFE_INTEGER) - (b.sorting ?? Number.MAX_SAFE_INTEGER))
        .map((funnel) => ({
          value: funnel.id,
          label: funnel.name,
        })),
    [funnels],
  )

  useEffect(() => {
    if (funnelOptions.length === 0) {
      setSelectedFunnelId(null)
      return
    }
    setSelectedFunnelId((prev) => prev ?? Number(funnelOptions[0].value))
  }, [funnelOptions])

  const handleChange = (uniqueName: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [uniqueName]: value,
    }))
    setErrors((prev) => ({ ...prev, [uniqueName]: '' }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isFranchiseManager && franchisesEnabled && !selectedFranchiseId) {
      addAlert({ type: 'error', title: 'Выберите франшизу для нового лида' })
      return
    }
    const resultValidation = validateForm(formData, visibleFields)
    if (resultValidation !== true) {
      setErrors(resultValidation)
      return
    }

    setLoading(true)

    const requestData: RequestCreateLead = {
      fields: fields
        .filter((field) => field.is_active)
        .map((field) => ({
          field_id: field.id,
          value: formData[field.unique_name] || '',
        })),
      funnel_id: selectedFunnelId ?? null,
      franchise_id: selectedFranchiseId,
      manager_id:
        ((typeof addManagerId === 'number' ||
          typeof addManagerId === 'string') &&
          addManagerId) ||
        null,
    }

    crmApi
      .insertLead(requestData)
      .then((res) => {
        if (isError(res)) throw new Error()
        const dataR = res?.data?.data as any

        addAlert({
          title: 'Успех',
          text: dataR?.message || 'Лид успешно добавлен',
          type: 'success',
        })

        setFormData({})
        setErrors({})
        onSuccess(dataR?.lead as InsertLeadApiLead | undefined)
      })
      .catch((error) => {
        addAlert({
          title: 'Ошибка',
          text: 'Ошибка при добавлении лида',
          type: 'error',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const renderField = (field: Field, error: string) => {
    const fieldProps = {
      id: field.unique_name,
      name: field.unique_name,
      value: formData[field.unique_name] ?? '',
      onChange: (
        e:
          | React.ChangeEvent<
              HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
            >
          | string,
      ) =>
        handleChange(
          field.unique_name,
          typeof e === 'string' ? e : e.target.value,
        ),
      error,
      placeholder: field.type === 'phone_number' ? field.name : undefined,
    }

    return (
      <VariableLeadField
        type={field.type}
        fieldProps={{ field, ...fieldProps }}
      />
    )
  }

  return (
    <>
      <div
        className={cn(
          mclasses.formWrapper,
          mclasses.overflowAuto,
          mclasses.gapXL,
          'custom-scroll',
        )}
      >
        <div>
          <label htmlFor="create-lead-funnel" className={mclasses.label}>
            Воронка
          </label>
          <Select
            showSearch
            placeholder="Выберите воронку"
            options={funnelOptions}
            value={selectedFunnelId ?? undefined}
            onChange={(value) =>
              setSelectedFunnelId(
                value === undefined || value === null || value === ''
                  ? null
                  : Number(value),
              )
            }
          />
        </div>

        {isFranchiseManager && franchisesEnabled && (
          <div>
            <label htmlFor="create-lead-franchise" className={mclasses.label}>
              Франшиза <span className={classes.requiredMark}> *</span>
            </label>
            <Select
              showSearch
              placeholder="Выберите франшизу"
              options={franchiseOptions}
              value={selectedFranchiseId ?? undefined}
              disabled={franchisesLoading}
              onChange={(value) =>
                setSelectedFranchiseId(
                  value === undefined || value === null || value === ''
                    ? null
                    : Number(value),
                )
              }
            />
          </div>
        )}

        {visibleFields
          .map((field) => (
            <div key={field.id}>
              <label htmlFor={field.unique_name} className={mclasses.label}>
                {field.name}
                {field.is_required && (
                  <span className={classes.requiredMark}> *</span>
                )}
              </label>
              {renderField(field, errors[field.unique_name] ?? '')}
            </div>
          ))}
      </div>
      <CheckBoxLabel
        className={classes.checkBoxLabel}
        value={!(addManagerId === false)}
        onClick={() =>
          setAddManagerId((prev) => (prev === false ? null : false))
        }
        label="Выбрать менеджера (перевести в работу)"
      />
      {addManagerId !== false && (
        <Select
          showSearch
          placeholder="Выберите менеджера"
          options={managers.map((manager) => ({
            label: manager.fio,
            value: manager.id,
          }))}
          onChange={setAddManagerId}
          value={addManagerId}
        />
      )}
      <div className={mclasses.actions}>
        <Button disabled={loading} onClick={handleSubmit}>
          Добавить
        </Button>
      </div>
    </>
  )
}

const CreateOneModalContentFull: FC<CreateOneModalContentProps> = ({
  onClose,
  ...props
}) => {
  return (
    <div className={mclasses.modalContainer}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>Добавить Лида</span>
        </div>
        <div className={mclasses.closeIcon} onClick={() => onClose()}>
          <IcoRemove />
        </div>
      </div>
      <CreateOneModalContent onClose={onClose} {...props} />
    </div>
  )
}

export default CreateOneModalContentFull
export {
  CreateOneModalContent as CreateOneModalContentClear,
  CreateOneModalContentFull,
}
